import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import Select from "react-select";
import toast from "react-hot-toast";
import { isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import Pagination from "./Pagination";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";
import { hrmsSuccessMessage } from "../utils/hrmsSuccessMessage";
import { formatInr } from "../utils/hrmsMoney";
import { HrmsBranchSelect } from "./HrmsBranchSelect";
import {
  fieldControlStyle,
  withSelectErrorStyles,
} from "../utils/lineItemFieldErrors";
import { useHrmsEmployees } from "../hooks/useHrmsEmployees";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";
import {
  LockIconGrey,
  LockIconSizeTable,
} from "../utils/LockIconforPermission";

const OVERTIME_LOCK_ICON = "solar:lock-bold";

function OvertimeEditLockedHint({ title }) {
  return (
    <span
      className="d-inline-flex align-items-center"
      title={title || "No permission to edit overtime (HRMS_OVERTIME_EDIT)."}
      role="img"
      aria-label="No permission to edit overtime"
    >
      <Icon
        icon={OVERTIME_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
    </span>
  );
}

function badgeClass(status) {
  const u = String(status || "").toUpperCase();
  if (u === "APPROVED") return "bg-success-subtle text-success";
  if (u === "REJECTED") return "bg-danger-subtle text-danger";
  if (u === "PENDING") return "bg-warning-subtle text-warning";
  return "bg-secondary-subtle text-secondary";
}

export function OvertimeTab({ branchId, onBranchChange, branchOptions, branchesLoading }) {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.HRMS_OVERTIME_VIEW);
  const canAdd = hasPermission(PERMISSIONS.HRMS_OVERTIME_ADD);
  const canEdit = hasPermission(PERMISSIONS.HRMS_OVERTIME_EDIT);
  const canApprove = hasPermission(PERMISSIONS.HRMS_OVERTIME_APPROVE);
  const editLockedTitle =
    "No permission to edit overtime (HRMS_OVERTIME_EDIT).";

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [employeeFilter, setEmployeeFilter] = useState(null);

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState(false);

  const { selectOptions, loading: empLoading } = useHrmsEmployees(branchId);

  useEffect(() => {
    setRows([]);
  }, [branchId, month, currentPage, itemsPerPage]);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formEmp, setFormEmp] = useState(null);
  const [formDate, setFormDate] = useState("");
  const [formHours, setFormHours] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formRate, setFormRate] = useState("");
  const [formReason, setFormReason] = useState("");

  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const selectStyles = useMemo(
    () => ({
      control: (p) => ({
        ...p,
        minHeight: 44,
        minWidth: 200,
        borderRadius: buttonTableStyle === "square" ? 0 : 8,
        borderColor: "#e0e6eb",
        boxShadow: "none",
      }),
      menu: (p) => ({ ...p, zIndex: 9999 }),
    }),
    [buttonTableStyle],
  );

  const pageCache = useVisitedPageCache();

  const fetchList = useCallback(async (force = false) => {
    const cacheKey = visitedPageKey([
      branchId,
      String(month || "").trim(),
      currentPage,
      itemsPerPage,
    ]);
    if (force) pageCache.clear();
    const cached = force ? null : pageCache.read(cacheKey);
    if (cached && branchId && canView) {
      setRows(cached.items);
      setPagination(cached.pagination);
      setLoading(false);
      return;
    }
    if (!branchId || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await hrmsApi("overtime_list", {
        branch_id: Number(branchId),
        month: String(month).trim(),
        pagination: { page: currentPage, limit: itemsPerPage },
      });
      if (isErpApiSuccess(res) && res.data) {
        const raw = Array.isArray(res.data.data) ? res.data.data : [];
        const p = res.data.pagination || {};
        const total = Number(p.total) ?? raw.length;
        const lim = Number(p.limit) || itemsPerPage;
        const nextPagination = {
          total,
          total_pages:
            Number(p.total_pages) || Math.max(1, Math.ceil(total / lim)),
        };
        setRows(raw);
        setPagination(nextPagination);
        pageCache.write(cacheKey, { items: raw, pagination: nextPagination });
      } else {
        setRows([]);
        if (res?.success === false && res?.message) toast.error(res.message);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load overtime.");
    } finally {
      setLoading(false);
    }
  }, [branchId, month, currentPage, itemsPerPage, canView]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [branchId, month]);

  const filteredRows = useMemo(() => {
    if (!employeeFilter?.value) return rows;
    const ua = Number(employeeFilter.value);
    return rows.filter((r) => Number(r.user_admin_id) === ua);
  }, [rows, employeeFilter]);

  const displayRows = employeeFilter?.value ? filteredRows : rows;

  const calcAmount = (r) => {
    const h = Number(r.hours);
    const rate = Number(r.rate_per_hour);
    if (Number.isFinite(r.amount)) return Number(r.amount);
    if (Number.isFinite(h) && Number.isFinite(rate)) return h * rate;
    return null;
  };

  const openAdd = () => {
    if (!canAdd) return;
    setEditingId(null);
    setFormEmp(null);
    setFormDate("");
    setFormHours("");
    setFormRate("");
    setFormReason("");
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    const ua = Number(r.user_admin_id);
    const opt = selectOptions.find((o) => Number(o.value) === ua);
    setFormEmp(
      opt || {
        value: ua,
        label: String(r.user_name || "").trim() || `User #${ua}`,
      },
    );
    setFormDate(String(r.work_date || "").split("T")[0]);
    setFormHours(String(r.hours ?? ""));
    setFormRate(String(r.rate_per_hour ?? ""));
    setFormReason(String(r.reason ?? ""));
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!branchId) return;
    if (editingId == null && !canAdd) {
      toast.error("You do not have permission to add overtime.");
      return;
    }
    if (editingId != null && !canEdit) {
      toast.error("You do not have permission to edit overtime.");
      return;
    }
    const next = {};
    const messages = [];
    if (!formEmp?.value) {
      next.employee = true;
      messages.push("Select employee.");
    }
    const hours = Number(String(formHours).replace(/,/g, ""));
    if (!Number.isFinite(hours) || hours <= 0) {
      next.hours = true;
      messages.push("Enter valid hours.");
    }
    setFieldErrors(next);
    if (messages.length) {
      toast.error(messages[0]);
      return;
    }
    const rateRaw = String(formRate).replace(/,/g, "").trim();
    const body = {
      branch_id: Number(branchId),
      user_admin_id: Number(formEmp.value),
      work_date: String(formDate || "").trim(),
      hours,
      reason: String(formReason || "").trim(),
    };
    if (rateRaw !== "" && Number.isFinite(Number(rateRaw))) {
      body.rate_per_hour = Number(rateRaw);
    }
    if (editingId != null) body.id = Number(editingId);
    setSaving(true);
    try {
      const res = await hrmsApi("overtime_save", body);
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(
            res,
            editingId != null
              ? "Overtime updated."
              : "Overtime added.",
          ),
        );
        setShowForm(false);
        await fetchList(true);
      } else {
        toast.error(res?.message || "Could not save.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not save overtime.");
    } finally {
      setSaving(false);
    }
  };

  const doApprove = async (r) => {
    if (!canApprove) return;
    try {
      const res = await hrmsApi("overtime_approve", {
        branch_id: Number(branchId),
        id: Number(r.id),
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(res, "Overtime approved."),
        );
        await fetchList(true);
      } else {
        toast.error(res?.message || "Could not approve.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not approve.");
    }
  };

  const submitReject = async () => {
    if (!rejectRow || !canApprove) return;
    const reason = String(rejectReason || "").trim();
    if (!reason) {
      setFieldErrors((prev) => ({ ...prev, rejectReason: true }));
      toast.error("Enter rejection reason.");
      return;
    }
    setFieldErrors((prev) => {
      if (!prev.rejectReason) return prev;
      const next = { ...prev };
      delete next.rejectReason;
      return next;
    });
    setRejecting(true);
    try {
      const res = await hrmsApi("overtime_reject", {
        branch_id: Number(branchId),
        id: Number(rejectRow.id),
        reason,
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(res, "Overtime rejected."),
        );
        setRejectRow(null);
        setRejectReason("");
        await fetchList(true);
      } else {
        toast.error(res?.message || "Could not reject.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not reject.");
    } finally {
      setRejecting(false);
    }
  };

  const colCount = 8;

  return (
    <div className="row g-3">
      <div className="col-12 d-flex flex-wrap align-items-end gap-3">
        <HrmsBranchSelect
          branchId={branchId}
          onChangeBranchId={onBranchChange}
          branchOptions={branchOptions}
          loading={branchesLoading}
          buttonTableStyle={buttonTableStyle}
        />
        <div>
          <Form.Control
            type="month"
            value={month}
            onChange={(ev) => setMonth(ev.target.value)}
            disabled={!canView}
            aria-label="Month"
            title="Month"
            style={{ minWidth: 160, minHeight: 44 }}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <Select
            inputId="hrms-ot-emp"
            isClearable
            isLoading={empLoading}
            options={selectOptions}
            value={employeeFilter}
            onChange={setEmployeeFilter}
            placeholder="All employees"
            styles={selectStyles}
            isDisabled={!canView}
          />
        </div>
        {canAdd && (
          <Button
            type="button"
            className={`btn btn-success ${buttonClass}`}
            disabled={!branchId || !canView}
            onClick={openAdd}
          >
            <Icon icon="solar:add-circle-bold" className="me-1" width={18} />
            Add
          </Button>
        )}
      </div>

      <div className="col-12">
        <div
          className={`table-responsive border ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
        >
          <table
            className={`table text-nowrap mb-0 align-middle ${tableClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
          >
            <thead className="text-dark fs-4">
              <tr>
                <th>Employee</th>
                <th>Work date</th>
                <th className="text-end">Hours</th>
                <th className="text-end">Rate / hr</th>
                <th className="text-end">Amount</th>
                <th className="text-wrap" style={{ minWidth: "10rem" }}>
                  Reason
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!canView ? (
                <tr>
                  <td colSpan={colCount} className="text-center py-5 text-muted">
                    You do not have permission to view overtime.
                  </td>
                </tr>
              ) : displayRows.length > 0 ? (
                displayRows.map((r) => {
                  const st = String(r.approval_status || "PENDING").toUpperCase();
                  const pending = st === "PENDING";
                  const amt = calcAmount(r);
                  return (
                    <tr key={r.id}>
                      <td>{String(r.user_name || "").trim() || "—"}</td>
                      <td>{String(r.work_date || "").split("T")[0]}</td>
                      <td className="text-end">{r.hours ?? "—"}</td>
                      <td className="text-end">{formatInr(r.rate_per_hour)}</td>
                      <td className="text-end fw-semibold">
                        {amt != null && Number.isFinite(amt) ? formatInr(amt) : "—"}
                      </td>
                      <td className="text-wrap">{String(r.reason || "—")}</td>
                      <td>
                        <span className={`badge ${badgeClass(st)}`}>{st}</span>
                      </td>
                      <td>
                        {canEdit && pending ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            className={`me-1 ${buttonClass}`}
                            onClick={() => openEdit(r)}
                          >
                            <Icon icon="solar:pen-bold" width={16} />
                          </Button>
                        ) : !canEdit ? (
                          <span className={canApprove && pending ? "me-2" : ""}>
                            <OvertimeEditLockedHint title={editLockedTitle} />
                          </span>
                        ) : null}
                        {canApprove && pending ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant="success"
                              className={`me-1 ${buttonClass}`}
                              onClick={() => doApprove(r)}
                            >
                              Approve
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-danger"
                              className={buttonClass}
                              onClick={() => {
                                setRejectRow(r);
                                setRejectReason("");
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              ) : !loading ? (
                <tr>
                  <td colSpan={colCount} className="text-center py-5 text-muted">
                    No overtime records.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {canView && !employeeFilter?.value && pagination.total > 0 ? (
        <div className="col-12">
          <Pagination
            totalItems={pagination.total}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      ) : null}

      <Modal show={showForm} onHide={() => !saving && setShowForm(false)} centered size="lg">
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingId ? "Edit overtime" : "Add overtime"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitForm}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Employee </Form.Label>
              <Select
                inputId="hrms-ot-form-emp"
                options={selectOptions}
                value={formEmp}
                onChange={setFormEmp}
                isDisabled={saving || !!editingId}
                styles={withSelectErrorStyles(selectStyles, fieldErrors.employee)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Work date </Form.Label>
              <Form.Control
                type="date"
                value={formDate}
                onChange={(ev) => setFormDate(ev.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hours </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formHours}
                onChange={(ev) => setFormHours(ev.target.value)}
                disabled={saving}
                placeholder="Enter hours"
                style={fieldControlStyle(fieldErrors.hours)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rate / hour</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formRate}
                onChange={(ev) => setFormRate(ev.target.value)}
                disabled={saving}
                placeholder="Uses profile OT rate if empty"
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formReason}
                onChange={(ev) => setFormReason(ev.target.value)}
                disabled={saving}
                placeholder="Enter reason"
              />
            </Form.Group>
            {(() => {
              const h = Number(formHours);
              const rt = Number(String(formRate).replace(/,/g, ""));
              if (Number.isFinite(h) && Number.isFinite(rt) && rt > 0) {
                return (
                  <p className="small text-muted mt-2 mb-0">
                    Amount (preview): <strong>{formatInr(h * rt)}</strong> (hours × rate)
                  </p>
                );
              }
              return null;
            })()}
          </Modal.Body>
          <Modal.Footer>

            <Button
              type="submit"
              variant="success"
              className={buttonClass}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!rejectRow} onHide={() => !rejecting && setRejectRow(null)} centered>
        <Modal.Header closeButton={!rejecting}>
          <Modal.Title>Reject overtime</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(ev) => setRejectReason(ev.target.value)}
              disabled={rejecting}
              style={fieldControlStyle(fieldErrors.rejectReason)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" disabled={rejecting} onClick={() => setRejectRow(null)}>
            Cancel
          </Button>
          <Button variant="danger" className={buttonClass} disabled={rejecting} onClick={submitReject}>
            {rejecting ? "Rejecting…" : "Reject"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
